import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Calendar, ChevronDown } from "lucide-react";
import type { DateRange } from "react-day-picker";

interface DateRangePresetsProps {
  onSelect: (range: DateRange) => void;
  className?: string;
}

type PresetKey =
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_14_days"
  | "last_30_days"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "last_year"
  | "mtd"
  | "ytd";

interface Preset {
  key: PresetKey;
  label: string;
  getRange: () => DateRange;
}

const getStartOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
};

const getEndOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() + (6 - day);
  return new Date(d.setDate(diff));
};

const getStartOfQuarter = (date: Date): Date => {
  const quarter = Math.floor(date.getMonth() / 3);
  return new Date(date.getFullYear(), quarter * 3, 1);
};

const getEndOfQuarter = (date: Date): Date => {
  const quarter = Math.floor(date.getMonth() / 3);
  return new Date(date.getFullYear(), quarter * 3 + 3, 0);
};

const presets: Preset[] = [
  {
    key: "today",
    label: "Today",
    getRange: () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return { from: today, to: today };
    },
  },
  {
    key: "yesterday",
    label: "Yesterday",
    getRange: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      return { from: yesterday, to: yesterday };
    },
  },
  {
    key: "last_7_days",
    label: "Last 7 Days",
    getRange: () => {
      const today = new Date();
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      from.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return { from, to: today };
    },
  },
  {
    key: "last_14_days",
    label: "Last 14 Days",
    getRange: () => {
      const today = new Date();
      const from = new Date(today);
      from.setDate(from.getDate() - 13);
      from.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return { from, to: today };
    },
  },
  {
    key: "last_30_days",
    label: "Last 30 Days",
    getRange: () => {
      const today = new Date();
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      from.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return { from, to: today };
    },
  },
  {
    key: "this_week",
    label: "This Week",
    getRange: () => {
      const today = new Date();
      const from = getStartOfWeek(today);
      from.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return { from, to: today };
    },
  },
  {
    key: "last_week",
    label: "Last Week",
    getRange: () => {
      const today = new Date();
      const lastWeek = new Date(today);
      lastWeek.setDate(lastWeek.getDate() - 7);
      const from = getStartOfWeek(lastWeek);
      const to = getEndOfWeek(lastWeek);
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);
      return { from, to };
    },
  },
  {
    key: "this_month",
    label: "This Month",
    getRange: () => {
      const today = new Date();
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      from.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return { from, to: today };
    },
  },
  {
    key: "last_month",
    label: "Last Month",
    getRange: () => {
      const today = new Date();
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);
      return { from, to };
    },
  },
  {
    key: "this_quarter",
    label: "This Quarter",
    getRange: () => {
      const today = new Date();
      const from = getStartOfQuarter(today);
      from.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return { from, to: today };
    },
  },
  {
    key: "last_quarter",
    label: "Last Quarter",
    getRange: () => {
      const today = new Date();
      const lastQuarterDate = new Date(today);
      lastQuarterDate.setMonth(lastQuarterDate.getMonth() - 3);
      const from = getStartOfQuarter(lastQuarterDate);
      const to = getEndOfQuarter(lastQuarterDate);
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);
      return { from, to };
    },
  },
  {
    key: "this_year",
    label: "This Year",
    getRange: () => {
      const today = new Date();
      const from = new Date(today.getFullYear(), 0, 1);
      from.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return { from, to: today };
    },
  },
  {
    key: "last_year",
    label: "Last Year",
    getRange: () => {
      const today = new Date();
      const from = new Date(today.getFullYear() - 1, 0, 1);
      const to = new Date(today.getFullYear() - 1, 11, 31);
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);
      return { from, to };
    },
  },
  {
    key: "mtd",
    label: "Month to Date",
    getRange: () => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      from.setHours(0, 0, 0, 0);
      yesterday.setHours(0, 0, 0, 0);
      return { from, to: yesterday };
    },
  },
  {
    key: "ytd",
    label: "Year to Date",
    getRange: () => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const from = new Date(today.getFullYear(), 0, 1);
      from.setHours(0, 0, 0, 0);
      yesterday.setHours(0, 0, 0, 0);
      return { from, to: yesterday };
    },
  },
];

const groupedPresets = {
  quick: ["today", "yesterday", "last_7_days", "last_14_days", "last_30_days"],
  weekly: ["this_week", "last_week"],
  monthly: ["this_month", "last_month", "mtd"],
  quarterly: ["this_quarter", "last_quarter"],
  yearly: ["this_year", "last_year", "ytd"],
};

export default function DateRangePresets({
  onSelect,
  className,
}: DateRangePresetsProps) {
  const handleSelect = (preset: Preset) => {
    const range = preset.getRange();
    onSelect(range);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={`h-[36px] ${className || ""}`}>
          <Calendar className="h-4 w-4 mr-2" />
          Presets
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[180px]">
        <DropdownMenuLabel className="text-xs text-gray-500">Quick</DropdownMenuLabel>
        {presets
          .filter((p) => groupedPresets.quick.includes(p.key))
          .map((preset) => (
            <DropdownMenuItem key={preset.key} onClick={() => handleSelect(preset)} className="cursor-pointer">
              {preset.label}
            </DropdownMenuItem>
          ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-gray-500">Weekly</DropdownMenuLabel>
        {presets
          .filter((p) => groupedPresets.weekly.includes(p.key))
          .map((preset) => (
            <DropdownMenuItem key={preset.key} onClick={() => handleSelect(preset)} className="cursor-pointer">
              {preset.label}
            </DropdownMenuItem>
          ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-gray-500">Monthly</DropdownMenuLabel>
        {presets
          .filter((p) => groupedPresets.monthly.includes(p.key))
          .map((preset) => (
            <DropdownMenuItem key={preset.key} onClick={() => handleSelect(preset)} className="cursor-pointer">
              {preset.label}
            </DropdownMenuItem>
          ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-gray-500">Quarterly</DropdownMenuLabel>
        {presets
          .filter((p) => groupedPresets.quarterly.includes(p.key))
          .map((preset) => (
            <DropdownMenuItem key={preset.key} onClick={() => handleSelect(preset)} className="cursor-pointer">
              {preset.label}
            </DropdownMenuItem>
          ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-gray-500">Yearly</DropdownMenuLabel>
        {presets
          .filter((p) => groupedPresets.yearly.includes(p.key))
          .map((preset) => (
            <DropdownMenuItem key={preset.key} onClick={() => handleSelect(preset)} className="cursor-pointer">
              {preset.label}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { presets, type PresetKey };
